import { AccessMode, OfferChannel, OfferStatus } from '@today/types'
import { z } from 'zod'
import { localeTag } from '../product/product.schema.js'

// ─── Shared primitives ────────────────────────────────────────────────────────

/**
 * The offer landing call-to-action. `href` MUST be a well-formed `https://` URL —
 * any other scheme (`http:`, `javascript:`, `data:`), a relative path or a
 * malformed value is rejected here, at the model boundary, so a hostile href can
 * never reach the public landing's `<a href>` (spec REQ-002 / Security). Validated
 * in the schema (not the service) so the whole offer create body is a single
 * `VALIDATION_ERROR` surface.
 */
export const CTA_HREF_MAX = 2048

const httpsUrl = z
	.string()
	.min(1)
	.max(CTA_HREF_MAX)
	.refine(
		(value) => {
			// Reject any leading/trailing whitespace BEFORE parsing: the WHATWG `URL`
			// parser strips leading ASCII tab/LF/CR, so `"\thttps://x"` would otherwise
			// parse as https and be stored verbatim — a value that is not the
			// "well-formed https:// URL" this field promises.
			if (value !== value.trim()) return false
			try {
				return new URL(value).protocol === 'https:'
			} catch {
				return false
			}
		},
		{ message: 'cta.href must be a well-formed https:// URL' },
	)

const offerCta = z
	.object({
		label: z.string().min(1).max(255),
		href: httpsUrl,
	})
	.strict()

// ─── Offers (TO-339) ──────────────────────────────────────────────────────────

/** Max length of a sender-set landing access code (CODE mode). */
export const ACCESS_CODE_MAX = 128

/**
 * Landing access policy (ADR-0051, TO-454). Optional on create; absent ⇒ NO_AUTH.
 * The cross-field rule (enforced by `superRefine`):
 *   - `CODE`      → `code` is REQUIRED and non-empty (the sender's shared code).
 *   - `NO_AUTH`   → `code` MUST be absent (a code on a public landing is meaningless).
 *   - `OTP`/`MAGIC_LINK` → `code` is ignored (the code/link is machine-generated and
 *     channel-delivered, not sender-set); supplying one is rejected to avoid confusion.
 * The code is plaintext here (the request body); the service hashes it before persisting.
 */
const offerAccess = z
	.object({
		mode: z.enum(AccessMode),
		code: z.string().min(1).max(ACCESS_CODE_MAX).optional(),
	})
	.strict()
	.superRefine((value, ctx) => {
		if (value.mode === AccessMode.CODE && !value.code) {
			ctx.addIssue({ code: 'custom', path: ['code'], message: 'code is required when access mode is CODE' })
		}
		if (value.mode !== AccessMode.CODE && value.code !== undefined) {
			ctx.addIssue({ code: 'custom', path: ['code'], message: `code is not allowed when access mode is ${value.mode}` })
		}
	})

/** The longest offer message (`introduction`), in UTF-16 code units. */
export const OFFER_INTRODUCTION_MAX = 2000

/**
 * A nullable/optional message field for the PATCH-style schemas: a blank or
 * whitespace-only string is normalized to `null` ("no message") so the stored
 * and contract value is always the canonical `null`, never `""` (TO-124).
 */
const offerIntroductionPatch = z
	.string()
	.max(OFFER_INTRODUCTION_MAX)
	.nullable()
	.optional()
	.transform((value) => (typeof value === 'string' && value.trim() === '' ? null : value))

/**
 * An offer's expiry instant (TO-125): ISO-8601 with `Z` or an offset, or `null`
 * for no expiry. The future / ≤ 365-day bound depends on the request instant, so
 * the service checks it, not the schema.
 */
const offerExpiresAt = z.iso.datetime({ offset: true }).nullable()

/**
 * `POST /offers`. Only `customerId` is required (TO-125 minimal draft): products,
 * locale and CTA may follow while the offer is unsent, and the send refuses an
 * incomplete offer. `productIds` order is significant (drives landing product
 * order and legal-text resolution order). An omitted `locale` defaults to the
 * tenant's default locale; an omitted `expiresAt` inherits the tenant default.
 * Server-owned fields (status, tenantId, ids, artifact refs) are forbidden by
 * `.strict()`.
 */
export const offerCreateSchema = z
	.object({
		customerId: z.uuid(),
		productIds: z.array(z.uuid()).min(1).optional(),
		locale: localeTag.optional(),
		cta: offerCta.optional(),
		introduction: z.string().min(1).max(OFFER_INTRODUCTION_MAX).optional(),
		access: offerAccess.optional(),
		expiresAt: offerExpiresAt.optional(),
	})
	.strict()

/**
 * `PATCH /offers/{id}` (TO-464) — a partial of the create's customer-visible
 * fields with the same per-field rules, except `introduction`: a PATCH can
 * clear it, so a blank string is normalized to `null` here rather than rejected
 * as it is on create. `customerId` is immutable and every server-owned field is
 * forbidden by `.strict()`; an empty object is rejected. `introduction: null`
 * (or a blank string, normalized to `null`) clears it; `expiresAt: null` removes
 * the expiry (TO-125).
 */
export const offerPatchSchema = z
	.object({
		productIds: z.array(z.uuid()).min(1).optional(),
		locale: localeTag.optional(),
		cta: offerCta.optional(),
		introduction: offerIntroductionPatch,
		access: offerAccess.optional(),
		expiresAt: offerExpiresAt.optional(),
		// The notification message (TO-140): a linked template, or a custom Liquid
		// body. Shape only — the body bounds, the parse and the template's
		// availability are the service's coded 422s.
		notificationTemplateId: z.uuid().nullable().optional(),
		notificationBody: z.string().nullable().optional(),
	})
	.strict()
	.refine((value) => Object.keys(value).length > 0, { message: 'at least one field is required' })
/** The most products one offer carries (matches the legal-text composer's bound). */
export const OFFER_MAX_PRODUCTS = 50

/**
 * `PUT /offers/{id}/products` (TO-123). The full ordered product set of a DRAFT
 * offer: 1–50 distinct product ids, array index = position (index 0 is the
 * landing's primary product).
 */
export const offerProductsReplaceSchema = z
	.object({
		productIds: z
			.array(z.uuid())
			.min(1)
			.max(OFFER_MAX_PRODUCTS)
			// Case-folded: Postgres `uuid` treats 'AB…' and 'ab…' as the same id.
			.refine((ids) => new Set(ids.map((id) => id.toLowerCase())).size === ids.length, {
				message: 'productIds must be distinct',
			}),
	})
	.strict()

/** `POST /offers/{id}/transitions`. `SENT` is not reachable here (service refuses it). */
export const offerTransitionSchema = z.object({ targetStatus: z.enum(OfferStatus) }).strict()

/** `POST /offers/{id}/send` (TO-465 REQ-002): 1–3 distinct channels; checked against the tenant's enabled set by the service. */
export const offerSendSchema = z
	.object({
		channels: z
			.array(z.enum(OfferChannel))
			.min(1)
			.max(3)
			.refine((channels) => new Set(channels).size === channels.length, { message: 'channels must be distinct' }),
	})
	.strict()

export type OfferCreateInput = z.infer<typeof offerCreateSchema>
export type OfferTransitionInput = z.infer<typeof offerTransitionSchema>
export type OfferSendInput = z.infer<typeof offerSendSchema>
export type OfferPatchInput = z.infer<typeof offerPatchSchema>
export type OfferProductsReplaceInput = z.infer<typeof offerProductsReplaceSchema>

/**
 * `POST /offers/{id}/preview` (TO-124). The unsaved Step 3 values the preview
 * renders instead of the stored ones: `locale` and `introduction` follow the
 * PATCH rules; an absent key uses the stored value, `introduction: null` (or a
 * blank string) renders no message. `{}` previews the stored offer.
 */
export const offerPreviewSchema = z
	.object({
		locale: localeTag.optional(),
		introduction: offerIntroductionPatch,
	})
	.strict()

export type OfferPreviewInput = z.infer<typeof offerPreviewSchema>
